import { selectedNoteTextAtom } from '@/atoms/selectedNoteTextAtoms';
import { useOpenEvidencePanel } from '@/hooks/useOpenEvidencePanel';
import { useSessionStage } from '@/hooks/useSessionStage';
import { useStageAskHeidiQuery } from '@/hooks/useStageAskHeidiQuery';
import { track } from '@/lib/analytics/track';
import { cn } from '@/lib/cn';
import {
  composeSelectionActionPrompt,
  type SelectionAiActionId,
} from '@/lib/selection-ai-prompts';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { Button } from '@heidi/design-system/button';
import { ButtonAction } from '@heidi/runtime/analytics-vocabulary';
import { useCurrentSessionId } from '@heidi/runtime/shell';
import { type Editor } from '@tiptap/react';
import { useSetAtom } from 'jotai';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { inlineSelectionEditRequestAtom } from './inline-selection-edit';
import {
  INLINE_EDIT_ACTION_IDS,
  isInlineInsightActionId,
  type SelectionActionItem,
} from './selection-ai-actions.config';
import { selectionAiActionsMessages } from './selection-ai-actions.messages';
import {
  ACTION_ROW_CLASS,
  isSelectionCheckGuidelinesAllowed,
  resolveSelectionActionTarget,
  type SelectionEditorActions,
} from './selection-ai-actions.utils';
import { SelectionMoreOptionsSubmenu } from './selection-more-options-submenu';
import { SelectionRewriteSubmenu } from './selection-rewrite-submenu';
import { useSelectionAiMenuShownTracking } from './use-selection-ai-menu-shown-tracking';

export type { SelectionEditorActions } from './selection-ai-actions.utils';

export const SelectionAiActions = ({
  editor,
  onActionStart,
  getTargetRange,
  editorActions,
  showTopBorder = true,
  trackShownOnSelectionUpdate = true,
}: {
  editor: Editor;
  /**
   * Notified as soon as any action fires — lets a host menu (e.g. the section
   * kebab dropdown) close itself before the action's flow takes over.
   */
  onActionStart?: () => void;
  /**
   * Overrides the live selection as the action target (e.g. the section kebab
   * targets the whole section body without selecting it — a real selection
   * would also summon the text bubble menu).
   */
  getTargetRange?: () => { from: number; to: number } | null;
  /**
   * Editor actions to group under the "More options" submenu. Omit to hide
   * the row (e.g. the section kebab).
   */
  editorActions?: SelectionEditorActions;
  /**
   * Draws the top divider that separates the AI actions from the formatting
   * toolbar above (bubble menu). The section kebab supplies its own
   * `DropdownMenuSeparator`, so it turns this off to avoid a double divider.
   */
  showTopBorder?: boolean;
  /**
   * The bubble-menu host tracks from its final visibility predicate. Other
   * hosts, such as the section kebab, keep the default and track from the
   * target lifecycle.
   */
  trackShownOnSelectionUpdate?: boolean;
}) => {
  const intl = useIntl();
  const sessionId = useCurrentSessionId();
  const sessionStage = useSessionStage();
  const openEvidencePanel = useOpenEvidencePanel();
  const stageAskHeidiQuery = useStageAskHeidiQuery(sessionId, openEvidencePanel);
  const setSelectedNoteText = useSetAtom(selectedNoteTextAtom);
  const setInlineEditRequest = useSetAtom(inlineSelectionEditRequestAtom);
  const { visibleV2Actions } = useSelectionAiMenuShownTracking({
    editor,
    getTargetRange,
    subscribeToSelectionUpdate: trackShownOnSelectionUpdate,
  });

  const handleActionClick = useCallback(
    (actionId: SelectionAiActionId) => {
      if (actionId === 'check_guidelines' && !isSelectionCheckGuidelinesAllowed()) {
        return;
      }
      const target = resolveSelectionActionTarget(editor, getTargetRange);
      if (!target) return;
      const { from, to, text } = target;
      onActionStart?.();

      track({
        event_name: TrackingEventName.ButtonClicked,
        props: {
          action: ButtonAction.AskHeidiSelectionActionClicked,
          label: actionId,
          selection_length: text.length,
          click_source: 'selection_menu',
          // Empty string off session pages — omit so warehouse joins aren't fabricated.
          session_id: sessionId || undefined,
          session_stage: sessionId ? sessionStage : undefined,
        },
        options: { amplitude: false },
      });

      // Check evidence needs the full Evidence retrieval stack — never inline.
      if (actionId === 'check_guidelines') {
        stageAskHeidiQuery({
          message: composeSelectionActionPrompt(actionId, text),
          entryMethod: 'selection_action',
          chipMeta: {
            label: actionId,
            category: 'selection',
            surface: 'selection_menu',
          },
        });
        setSelectedNoteText(null);
        editor.commands.setTextSelection(to);
        return;
      }

      const isInlineEdit = INLINE_EDIT_ACTION_IDS.has(actionId);
      if (isInlineInsightActionId(actionId)) {
        setInlineEditRequest({
          editor,
          actionId,
          from,
          to,
          text,
          intent: 'insight',
        });
        editor.commands.setTextSelection(to);
        return;
      }
      if (isInlineEdit) {
        setInlineEditRequest({
          editor,
          actionId,
          from,
          to,
          text,
          intent: 'edit',
        });
        editor.commands.setTextSelection(to);
        return;
      }

      stageAskHeidiQuery({
        message: composeSelectionActionPrompt(actionId, text),
        entryMethod: 'selection_action',
        chipMeta: {
          label: actionId,
          category: 'selection',
          surface: 'selection_menu',
        },
      });
      setSelectedNoteText(null);
      editor.commands.setTextSelection(to);
    },
    [
      editor,
      sessionId,
      sessionStage,
      stageAskHeidiQuery,
      setSelectedNoteText,
      setInlineEditRequest,
      onActionStart,
      getTargetRange,
    ]
  );

  const handleAskAnything = useCallback(() => {
    const target = resolveSelectionActionTarget(editor, getTargetRange);
    if (!target) return;
    const { from, to, text } = target;
    onActionStart?.();

    track({
      event_name: TrackingEventName.ButtonClicked,
      props: {
        action: ButtonAction.AskHeidiSelectionAskAnythingClicked,
        label: 'ask_anything',
        selection_length: text.length,
        click_source: 'selection_menu',
        session_id: sessionId || undefined,
        session_stage: sessionId ? sessionStage : undefined,
      },
      options: { amplitude: false },
    });

    setInlineEditRequest({
      editor,
      actionId: 'ask_anything',
      from,
      to,
      text,
      intent: 'edit',
      promptInput: true,
    });
    editor.commands.setTextSelection(to);
  }, [
    editor,
    sessionId,
    sessionStage,
    setInlineEditRequest,
    onActionStart,
    getTargetRange,
  ]);

  const renderActionRow = ({ id, icon: Icon, message }: SelectionActionItem) => (
    <button
      key={id}
      type="button"
      onClick={() => handleActionClick(id)}
      className={ACTION_ROW_CLASS}
      data-testid={`selection-ai-action-${id}`}
    >
      <Icon size={14} className="shrink-0 text-text-secondary" />
      {intl.formatMessage(message)}
    </button>
  );

  return (
    <div
      className={cn(
        'flex flex-col gap-0.5 p-2',
        showTopBorder && 'border-t border-border'
      )}
      data-testid="selection-ai-actions"
    >
      <SelectionRewriteSubmenu onActionClick={handleActionClick} />
      {visibleV2Actions.map(renderActionRow)}
      {editorActions ? (
        <SelectionMoreOptionsSubmenu editorActions={editorActions} />
      ) : null}
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={handleAskAnything}
        className="mt-0.5 w-full"
        data-testid="selection-ai-ask-heidi-to-edit"
      >
        {intl.formatMessage(selectionAiActionsMessages.askHeidiToEdit)}
      </Button>
    </div>
  );
};
